import { ApolloCache, ObservableQuery, Reference } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import uniqBy from 'lodash/uniqBy';
import {
  App,
  ChatRoomDetailsFragment,
  ChatRoomKind,
  ChatsQuery,
  FilterContentsQuery,
  MembersListQuery,
  MembersListTotalsQuery,
  MessageDetailsFragment,
  MessageDetailsFragmentDoc,
  NewMessagesDocument,
  NewMessagesSubscription,
  NewMessagesSubscriptionVariables,
  NewTotalUnreadMessageCountDocument,
  NewTotalUnreadMessageCountSubscription,
  NewTotalUnreadMessageCountSubscriptionVariables,
  SendMessageMutation,
} from '../graphql/types';
import { compareDateStrings } from '../utils/date';

export const mergeSelfMembersNewMessageSubscription = (
  prev: MembersListQuery,
  subscriptionData: NewMessagesSubscription,
  chatRoomId: string,
) => {
  if (!subscriptionData) {
    return prev;
  }
  const updateChatId = subscriptionData.newMessages.chatRoom.id;
  if (updateChatId !== chatRoomId) {
    return prev;
  }

  if (!prev.self?.coachData) {
    return prev;
  }

  const { chatRoom: newChatRoom, message: newMessage } =
    subscriptionData.newMessages;
  const newUnreadMessageCount = newChatRoom.messages.unreadMessageCount;
  const newLastMessageReceivedAt = newChatRoom.lastMessageReceivedAt;

  const mergedData = cloneDeep(prev);

  if (mergedData.self?.coachData?.coachUsers) {
    mergedData.self.coachData.coachUsers.edges =
      mergedData.self.coachData.coachUsers.edges
        .map((edge) => {
          const prevEdgeClone = cloneDeep(edge);
          if (
            edge.node.fitCoachChat?.id !== chatRoomId ||
            !prevEdgeClone?.node.fitCoachChat
          ) {
            return edge;
          }

          prevEdgeClone.node.fitCoachChat.messages.unreadMessageCount =
            newUnreadMessageCount;
          prevEdgeClone.node.fitCoachChat.lastMessageReceivedAt =
            newLastMessageReceivedAt;
          prevEdgeClone.node.fitCoachChat.messages.edges.push({
            node: newMessage,
          });

          return prevEdgeClone;
        })
        .sort((a, b) =>
          compareDateStrings(
            a.node.fitCoachChat?.lastMessageReceivedAt,
            b.node.fitCoachChat?.lastMessageReceivedAt,
          ),
        );
  }

  return mergedData;
};

export const membersListUnreadSubscribeToMore = (
  subscribeToMore: ObservableQuery<MembersListQuery>['subscribeToMore'],
  chatRoomId?: string | null,
) => {
  if (chatRoomId) {
    return subscribeToMore<
      NewMessagesSubscription,
      NewMessagesSubscriptionVariables
    >({
      document: NewMessagesDocument,
      variables: { chatId: chatRoomId },
      updateQuery: (prev, { subscriptionData }) =>
        // prev comes from the cache and is complete at runtime; v4 types it
        // as DeepPartial.
        mergeSelfMembersNewMessageSubscription(
          prev as MembersListQuery,
          subscriptionData.data,
          chatRoomId,
        ),
    });
  }
  return () => {};
};

export const mergeSelfChatRoomsNewMessageSubscription = (
  prev: ChatsQuery,
  subscriptionData: NewMessagesSubscription,
  chatRoomId: string,
) => {
  if (!subscriptionData) {
    return prev;
  }
  const updateChatId = subscriptionData.newMessages.chatRoom.id;
  if (updateChatId !== chatRoomId) {
    return prev;
  }

  if (!prev.self?.chatRooms) {
    return prev;
  }

  const { chatRoom: newChatRoom, message: newMessage } =
    subscriptionData.newMessages;
  const newUnreadMessageCount = newChatRoom.messages.unreadMessageCount;
  const newLastMessageReceivedAt = newChatRoom.lastMessageReceivedAt;

  const mergedData = cloneDeep(prev);

  if (mergedData.self) {
    mergedData.self.chatRooms.edges = mergedData.self.chatRooms.edges
      .map((edge) => {
        if (edge.node.id !== chatRoomId) {
          return edge;
        }
        const prevEdgeClone = cloneDeep(edge);

        prevEdgeClone.node.messages.unreadMessageCount = newUnreadMessageCount;
        prevEdgeClone.node.lastMessageReceivedAt = newLastMessageReceivedAt;
        prevEdgeClone.node.messages.edges.push({
          node: newMessage,
        });

        return prevEdgeClone;
      })
      .sort((a, b) =>
        compareDateStrings(
          a.node.lastMessageReceivedAt,
          b.node.lastMessageReceivedAt,
        ),
      );
  }

  return mergedData;
};

export const chatListUnreadSubscribeToMore = (
  subscribeToMore: ObservableQuery<ChatsQuery>['subscribeToMore'],
  chatRoomId?: string | null,
) => {
  if (chatRoomId) {
    return subscribeToMore<
      NewMessagesSubscription,
      NewMessagesSubscriptionVariables
    >({
      document: NewMessagesDocument,
      variables: { chatId: chatRoomId },
      updateQuery: (prev, { subscriptionData }) =>
        // prev comes from the cache and is complete at runtime; v4 types it
        // as DeepPartial.
        mergeSelfChatRoomsNewMessageSubscription(
          prev as ChatsQuery,
          subscriptionData.data,
          chatRoomId,
        ),
    });
  }
  return () => {};
};

export const multiChatTotalUnreadSubscribeToMore = (
  subscribeToMore: ObservableQuery<MembersListTotalsQuery>['subscribeToMore'],
) =>
  subscribeToMore<
    NewTotalUnreadMessageCountSubscription,
    NewTotalUnreadMessageCountSubscriptionVariables
  >({
    document: NewTotalUnreadMessageCountDocument,
    variables: {
      apps: [App.Heart],
      kinds: [ChatRoomKind.Direct],
    },
    updateQuery: (unsafePrev, { subscriptionData }) => {
      // prev comes from the cache and is complete at runtime; v4 types it as
      // DeepPartial.
      const prev = unsafePrev as MembersListTotalsQuery;
      if (!subscriptionData.data) {
        return prev;
      }
      const newCount = subscriptionData.data.newTotalUnreadMessageCount;

      if (!prev.self || !prev.self.coachData) {
        return prev;
      }

      const mergedData: MembersListTotalsQuery = {
        self: {
          ...prev.self,
          coachData: {
            ...prev.self.coachData,
            totalUnreadMessageCount: newCount,
          },
        },
      };

      return mergedData;
    },
  });

export const mergeChatRoom = <T extends ChatRoomDetailsFragment>(
  oldChatRoom: T,
  newSub: NewMessagesSubscription,
): T => {
  let replace = false;
  const {
    newMessages: { chatRoom: newChatRoom, message: newMessage, optimisticId },
  } = newSub;
  const newMessages = (oldChatRoom.messages.edges || []).map((edge) => {
    if (
      edge.node.id === newMessage.id ||
      (optimisticId && edge.node.id === `optimistic/${optimisticId}`)
    ) {
      replace = true;
      return { node: newMessage };
    }

    return edge;
  });

  if (!replace) {
    newMessages.push({ node: newMessage });
  }

  return {
    ...oldChatRoom,
    ...newChatRoom,
    messages: {
      ...oldChatRoom.messages,
      unreadMessageCount: newChatRoom.messages.unreadMessageCount,
      // Somehow we are still able to get duplicate ids here so remove them
      edges: uniqBy(newMessages, (n) => n.node.id),
    },
  };
};

export const mergeSentMessage = (
  cache: ApolloCache,
  existingMessages: { edges: { node: Reference }[] },
  result: { data?: SendMessageMutation | null },
) => {
  if (result.data) {
    const newMessage = cache.writeFragment<MessageDetailsFragment>({
      fragment: MessageDetailsFragmentDoc,
      fragmentName: 'MessageDetails',
      data: result.data.sendMessage,
      broadcast: true,
    });

    if (!newMessage) {
      return existingMessages;
    }

    if (
      existingMessages.edges?.some((e) => e.node.__ref === newMessage.__ref)
    ) {
      return existingMessages;
    }

    return {
      ...existingMessages,
      edges: [...(existingMessages.edges ?? []), { node: newMessage }],
    };
  }
  return existingMessages;
};

export const mergeFilteredContents = (
  prev: FilterContentsQuery,
  next: { fetchMoreResult: FilterContentsQuery },
) => {
  if (!next.fetchMoreResult) {
    return prev;
  }
  return {
    contents: {
      ...next.fetchMoreResult.contents,
      edges: [
        ...(prev.contents?.edges || []),
        ...next.fetchMoreResult.contents.edges,
      ],
    },
  };
};
